import { DateTime } from 'luxon';
import { normalizeAvailability } from './dateUtils';

// When two people are free at the same time (brief 032, review M1).
// Availability is stored as local 2-hour blocks + a zone; the UTC form is worked out
// here at read time and never stored. Times are compared as ranges in 15-minute
// steps, so zones an odd number of hours apart (New York ↔ London) or on the half
// hour (India) overlap properly, not only blocks that start at the same UTC minute.

const WEEK_MIN = 7 * 24 * 60;
const STEP = 15;
const CELLS = WEEK_MIN / STEP;
const BLOCK = 120;

const cellsOf = (utcSlots) => {
  const on = new Uint8Array(CELLS);
  utcSlots.forEach(({ startMin, endMin }) => {
    const dur = ((endMin - startMin) % WEEK_MIN + WEEK_MIN) % WEEK_MIN || BLOCK;
    for (let m = 0; m < dur; m += STEP) on[Math.floor((((startMin + m) % WEEK_MIN) + WEEK_MIN) % WEEK_MIN / STEP)] = 1;
  });
  return on;
};

// Shared free time as merged weekly-UTC ranges [{ startMin, endMin }], sorted. A range
// that runs past Sunday midnight UTC is one range whose endMin is past the week.
export const sharedRanges = (me, them, from = new Date()) => {
  const a = cellsOf(normalizeAvailability(me, from).utc);
  const b = cellsOf(normalizeAvailability(them, from).utc);
  const both = i => a[i % CELLS] && b[i % CELLS];
  if (!a.some((x, i) => x && b[i])) return [];
  if (a.every((x, i) => x && b[i])) return [{ startMin: 0, endMin: WEEK_MIN }];
  // Start the scan just after a gap, so a range across the week's end stays whole.
  let s = 0;
  while (both(s)) s++;
  const out = [];
  for (let i = s; i < s + CELLS; i++) {
    if (!both(i)) continue;
    const start = i;
    while (i < s + CELLS && both(i)) i++;
    const startMin = (start % CELLS) * STEP;
    out.push({ startMin, endMin: startMin + (i - start) * STEP });
  }
  return out.sort((x, y) => x.startMin - y.startMin);
};

// Whole 2-hour blocks of shared time, the number the 35/50/15 score weighs.
export const sharedBlocks = (ranges) =>
  Math.floor(ranges.reduce((n, r) => n + (r.endMin - r.startMin), 0) / BLOCK);

// Two-hour windows to offer for a session: starting on the hour in `zone`, back to
// back from the first such hour, each fully inside a shared range.
export const sessionWindows = (ranges, zone, from = new Date()) => {
  const monday = DateTime.fromJSDate(from, { zone: 'utc' }).startOf('week');
  const out = [];
  ranges.forEach(({ startMin, endMin }) => {
    let t = startMin;
    while (t + BLOCK <= endMin && monday.plus({ minutes: t }).setZone(zone || 'UTC').minute !== 0) t += STEP;
    for (; t + BLOCK <= endMin; t += BLOCK) out.push({ startMin: t % WEEK_MIN, endMin: (t + BLOCK) % WEEK_MIN });
  });
  const seen = new Set();
  return out.filter(w => !seen.has(w.startMin) && seen.add(w.startMin)).sort((x, y) => x.startMin - y.startMin);
};
